/** Configuration — Search & Matching — rules, options, and sample data.
 *  Status: NOT YET READY — deferred definitions remain in Section 8.
 */

export const SEARCH_MATCHING_ROUTE = '/admin/configuration/search-matching';

export const SEARCH_RULE_AREA_OPTIONS = [
  { value: 'keyword_search', label: 'Keyword Search' },
  { value: 'location_radius', label: 'Location & Radius' },
  { value: 'search_filters', label: 'Search Filters' },
  { value: 'search_result_order', label: 'Search Result Order' },
];

export const SEARCH_SOURCE_OPTIONS = [
  { value: 'applied_keywords', label: 'Applied Keywords' },
  { value: 'post_title', label: 'Post Title' },
  { value: 'post_description', label: 'Post Description' },
];

export const RADIUS_OPTIONS = [
  { value: '10', label: '10 km' },
  { value: '20', label: '20 km' },
  { value: '30', label: '30 km' },
  { value: '40', label: '40 km' },
  { value: '50', label: '50 km' },
];

export const SEARCH_FILTER_OPTIONS = [
  { value: 'categories', label: 'Categories' },
  { value: 'timeframe', label: 'Timeframe' },
];

/** Restricted Timeframe Values only. "Any time" is always offered when Timeframe is selected; it is not a stored Timeframe Value. */
export const TIMEFRAME_VALUE_OPTIONS = [
  { value: 'last_24_hours', label: 'Last 24 hours' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
];

export const RESULT_ORDER_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'nearest', label: 'Nearest' },
  { value: 'newest', label: 'Newest' },
];

export const KEYWORD_BEHAVIOUR_ROWS = [
  {
    key: 'required_words',
    label: 'Required search words',
    value: 'At least one word after word handling. Empty or punctuation-only entries show: Enter at least one search word.',
  },
  {
    key: 'word_handling',
    label: 'Word handling',
    value:
      'Ignore case and leading/trailing spaces; treat repeated spaces as one; remove apostrophes (U+0027, U+2018, U+2019); other punctuation separates words; check each resulting word separately.',
  },
  {
    key: 'matching',
    label: 'Matching',
    value:
      'A Post matches when at least one entered word matches a word in at least one selected source. 1–3 characters: exact whole-word. 4+: exact or one typing difference (add, remove, change, or swap neighbouring characters). No synonyms, source weighting or hidden relevance scores.',
  },
];

export const LOCATION_BEHAVIOUR_ROWS = [
  {
    key: 'distance',
    label: 'Geographic behaviour',
    value:
      'Measure distance from the active Search location to the Post’s location (suburb or postcode — measurement NOT DEFINED). Boundary inclusive. Business location and Post Service Radius do not replace Post location or act as an extra filter.',
  },
];

export const FILTER_BEHAVIOUR_ROWS = [
  {
    key: 'categories',
    label: 'Categories matching',
    value:
      'Match selected Trade Categories by permanent reference (OR across selections). Selecting none applies no restriction. Which Trade Categories the filter offers is NOT DEFINED.',
  },
  {
    key: 'timeframe',
    label: 'Timeframe meanings',
    value:
      'Uses Published On only. Any time: no restriction. Last 24 hours / 7 days / 30 days: restricted periods ending at Search time, boundary inclusive; one elapsed day = 24 hours. Future Published On does not satisfy a restricted period.',
  },
  {
    key: 'eligibility',
    label: 'Search Eligibility',
    value:
      'Requires search word, active Search location (NOT DEFINED), Radius from Available Radius, and all four Rules saved with no unavailable saved choice. Only published Posts with Display State Visible and Visibility Audience Public qualify.',
  },
];

export const ORDER_BEHAVIOUR_ROWS = [
  {
    key: 'featured',
    label: 'Featured sequence',
    value:
      'Featured Posts first; then distance shortest first; then Published On most recent first; then Post ID ascending. Featured status from Post Type (NOT CONFIRMED).',
  },
  {
    key: 'nearest',
    label: 'Nearest sequence',
    value: 'Distance shortest first; then Published On most recent first; then Post ID ascending.',
  },
  {
    key: 'newest',
    label: 'Newest sequence',
    value: 'Published On most recent first; then distance shortest first; then Post ID ascending.',
  },
];

export const DEVELOPER_NAME = 'Adeel Khan';

export const MESSAGES = {
  saved: 'Rule saved.',
  updated: 'Rule updated.',
  unchanged: 'No changes to save.',
  missingName: 'Enter a Rule Name.',
  nameUsed: 'This Rule Name is already in use.',
  missingArea: 'Select a Rule Area.',
  occupiedArea: 'A Rule already exists for this Rule Area',
  noSources: 'Select at least one Search Source.',
  noRadius: 'Select at least one Radius.',
  missingDefaultRadius: 'Select a Default Radius.',
  noFilters: 'Select at least one Search Filter.',
  missingTimeframes: 'Select at least one Timeframe value.',
  noOrders: 'Select at least one Result Order.',
  missingDefaultOrder: 'Select a Default Order.',
  unavailableSelection: 'Select another value for',
  staleEdit: 'This Rule has changed. Review the latest saved Rule.',
  permission: 'You do not have permission to save this Rule.',
  checksUnavailable: 'Required checks could not be completed. Try again.',
  saveFailed: 'The Rule could not be saved. Your changes have been kept for review.',
};

export const createDefaultSearchMatchingForm = () => ({
  id: '',
  ruleName: '',
  ruleArea: '',
  searchSources: [],
  availableRadius: [],
  defaultRadius: '',
  availableFilters: [],
  timeframeValues: [],
  availableOrders: [],
  defaultOrder: '',
  /** Values marked unavailable stay until replaced. What makes a choice unavailable is NOT DEFINED. */
  unavailableChoices: {},
  createdBy: '',
  createdOn: '',
  lastUpdatedBy: '',
  lastUpdatedOn: '',
  updatedBy: '',
  updatedOn: '',
  changeHistory: [],
});

export const labelFromOptions = (options, value) => {
  if (value == null || value === '') return '—';
  return options.find((o) => o.value === value)?.label || value;
};

export const getRuleAreaLabel = (value) => labelFromOptions(SEARCH_RULE_AREA_OPTIONS, value);

export const getSearchSourceLabel = (value) => labelFromOptions(SEARCH_SOURCE_OPTIONS, value);

export const getRadiusLabel = (value) => labelFromOptions(RADIUS_OPTIONS, value);

export const getSearchFilterLabel = (value) => labelFromOptions(SEARCH_FILTER_OPTIONS, value);

export const getTimeframeValueLabel = (value) => labelFromOptions(TIMEFRAME_VALUE_OPTIONS, value);

export const getResultOrderLabel = (value) => labelFromOptions(RESULT_ORDER_OPTIONS, value);

export const normalizeNameKey = (name) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const findDuplicateRuleName = (rules = [], name, excludeId) => {
  const key = normalizeNameKey(name);
  if (!key) return null;
  return (
    rules.find(
      (r) => r.id !== excludeId && normalizeNameKey(r.ruleName) === key,
    ) || null
  );
};

export const findRuleForArea = (rules = [], ruleArea, excludeId) => {
  if (!ruleArea) return null;
  return (
    rules.find((r) => r.ruleArea === ruleArea && r.id !== excludeId) || null
  );
};

export const formatSearchMatchingDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatSearchMatchingDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const nextSearchMatchingRuleId = (rules = []) => {
  let max = 0;
  rules.forEach((r) => {
    const m = String(r.id || '').match(/^SMR-(\d+)$/i);
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `SMR-${String(max + 1).padStart(4, '0')}`;
};

export const formatSelectedValues = (rule) => {
  const area = rule?.ruleArea;
  const unavailable = rule?.unavailableChoices || {};

  if (area === 'keyword_search') {
    const sources = (rule.searchSources || []).map((v) => {
      const label = getSearchSourceLabel(v);
      return (unavailable.searchSources || []).includes(v) ? `${label} (unavailable)` : label;
    });
    return sources.join(', ') || '—';
  }

  if (area === 'location_radius') {
    const radii = (rule.availableRadius || []).map((v) => {
      const label = getRadiusLabel(v);
      return (unavailable.availableRadius || []).includes(v) ? `${label} (unavailable)` : label;
    });
    return radii.join(', ') || '—';
  }

  if (area === 'search_filters') {
    const filters = (rule.availableFilters || []).map((v) => {
      const label = getSearchFilterLabel(v);
      return (unavailable.availableFilters || []).includes(v) ? `${label} (unavailable)` : label;
    });
    const hasTimeframe = (rule.availableFilters || []).includes('timeframe');
    if (!hasTimeframe) {
      return filters.length ? `Filters: ${filters.join(', ')}` : '—';
    }
    const timeframes = ['Any time', ...(rule.timeframeValues || []).map((v) => {
      const label = getTimeframeValueLabel(v);
      return (unavailable.timeframeValues || []).includes(v) ? `${label} (unavailable)` : label;
    })];
    return `Filters: ${filters.join(', ')}; Timeframes: ${timeframes.join(', ')}`;
  }

  if (area === 'search_result_order') {
    const orders = (rule.availableOrders || []).map((v) => {
      const label = getResultOrderLabel(v);
      return (unavailable.availableOrders || []).includes(v) ? `${label} (unavailable)` : label;
    });
    return orders.join(', ') || '—';
  }

  return '—';
};

export const formatDefaultValue = (rule) => {
  const area = rule?.ruleArea;
  const unavailable = rule?.unavailableChoices || {};

  if (area === 'keyword_search') return 'Not applicable';

  if (area === 'location_radius') {
    if (!rule.defaultRadius) return '—';
    const label = getRadiusLabel(rule.defaultRadius);
    return (unavailable.defaultRadius || []).includes(rule.defaultRadius)
      ? `${label} (unavailable)`
      : label;
  }

  if (area === 'search_filters') {
    if ((rule.availableFilters || []).includes('timeframe')) {
      return 'Timeframe: Any time (fixed)';
    }
    return 'Not applicable';
  }

  if (area === 'search_result_order') {
    if (!rule.defaultOrder) return '—';
    const label = getResultOrderLabel(rule.defaultOrder);
    return (unavailable.defaultOrder || []).includes(rule.defaultOrder)
      ? `${label} (unavailable)`
      : label;
  }

  return '—';
};

export const settingsForArea = (form = {}) => {
  const area = form.ruleArea;
  if (area === 'keyword_search') {
    return { searchSources: [...(form.searchSources || [])] };
  }
  if (area === 'location_radius') {
    return {
      availableRadius: [...(form.availableRadius || [])],
      defaultRadius: form.defaultRadius || '',
    };
  }
  if (area === 'search_filters') {
    const filters = [...(form.availableFilters || [])];
    return {
      availableFilters: filters,
      timeframeValues: filters.includes('timeframe')
        ? [...(form.timeframeValues || [])]
        : [],
    };
  }
  if (area === 'search_result_order') {
    return {
      availableOrders: [...(form.availableOrders || [])],
      defaultOrder: form.defaultOrder || '',
    };
  }
  return {};
};

export const clearSettingsOutsideArea = (form = {}) => {
  const area = form.ruleArea;
  return {
    ...form,
    searchSources: area === 'keyword_search' ? [...(form.searchSources || [])] : [],
    availableRadius: area === 'location_radius' ? [...(form.availableRadius || [])] : [],
    defaultRadius: area === 'location_radius' ? form.defaultRadius || '' : '',
    availableFilters: area === 'search_filters' ? [...(form.availableFilters || [])] : [],
    timeframeValues:
      area === 'search_filters' && (form.availableFilters || []).includes('timeframe')
        ? [...(form.timeframeValues || [])]
        : [],
    availableOrders: area === 'search_result_order' ? [...(form.availableOrders || [])] : [],
    defaultOrder: area === 'search_result_order' ? form.defaultOrder || '' : '',
  };
};

export const normalizeSearchMatchingForm = (raw = {}) => {
  const base = createDefaultSearchMatchingForm();
  const area = raw.ruleArea || '';
  const filters = Array.isArray(raw.availableFilters) ? [...raw.availableFilters] : [];
  return {
    ...base,
    ...raw,
    id: raw.id || '',
    ruleName: raw.ruleName || '',
    ruleArea: area,
    searchSources: Array.isArray(raw.searchSources) ? [...raw.searchSources] : [],
    availableRadius: Array.isArray(raw.availableRadius) ? [...raw.availableRadius] : [],
    defaultRadius: raw.defaultRadius || '',
    availableFilters: filters,
    timeframeValues:
      filters.includes('timeframe') && Array.isArray(raw.timeframeValues)
        ? [...raw.timeframeValues]
        : [],
    availableOrders: Array.isArray(raw.availableOrders) ? [...raw.availableOrders] : [],
    defaultOrder: raw.defaultOrder || '',
    unavailableChoices: raw.unavailableChoices && typeof raw.unavailableChoices === 'object'
      ? { ...raw.unavailableChoices }
      : {},
    createdBy: raw.createdBy || '',
    createdOn: raw.createdOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.updatedBy || '',
    lastUpdatedOn: raw.lastUpdatedOn || raw.updatedOn || '',
    updatedBy: raw.updatedBy || raw.lastUpdatedBy || '',
    updatedOn: raw.updatedOn || raw.lastUpdatedOn || '',
    changeHistory: Array.isArray(raw.changeHistory) ? [...raw.changeHistory] : [],
  };
};

export const hasUnavailableSavedChoice = (rule) => {
  const u = rule?.unavailableChoices || {};
  return Object.values(u).some((list) => Array.isArray(list) && list.length > 0);
};

/** Public Search is available only when all four Rules are saved and none has an unavailable saved choice. */
export const isPublicSearchAvailable = (rules = []) => {
  if (SEARCH_RULE_AREA_OPTIONS.length !== rules.length) return false;
  const areas = new Set(rules.map((r) => r.ruleArea));
  if (SEARCH_RULE_AREA_OPTIONS.some((o) => !areas.has(o.value))) return false;
  return !rules.some(hasUnavailableSavedChoice);
};

export const buildSampleSearchMatchingRules = () => [
  {
    ...createDefaultSearchMatchingForm(),
    id: 'SMR-0004',
    ruleName: 'Result Order',
    ruleArea: 'search_result_order',
    availableOrders: ['featured', 'nearest', 'newest'],
    defaultOrder: 'featured',
    createdBy: 'Daniel Lee',
    lastUpdatedBy: 'Daniel Lee',
    createdOn: '2026-08-17T08:50:00+10:00',
    lastUpdatedOn: '2026-08-17T08:50:00+10:00',
    updatedBy: 'Daniel Lee',
    updatedOn: '2026-08-17T08:50:00+10:00',
  },
  {
    ...createDefaultSearchMatchingForm(),
    id: 'SMR-0003',
    ruleName: 'Search Filters',
    ruleArea: 'search_filters',
    availableFilters: ['categories', 'timeframe'],
    timeframeValues: ['last_24_hours', 'last_7_days', 'last_30_days'],
    createdBy: 'Priya Shah',
    lastUpdatedBy: 'Priya Shah',
    createdOn: '2026-08-17T08:40:00+10:00',
    lastUpdatedOn: '2026-08-17T08:40:00+10:00',
    updatedBy: 'Priya Shah',
    updatedOn: '2026-08-17T08:40:00+10:00',
  },
  {
    ...createDefaultSearchMatchingForm(),
    id: 'SMR-0002',
    ruleName: 'Location and Radius',
    ruleArea: 'location_radius',
    availableRadius: ['10', '20', '30', '40', '50'],
    defaultRadius: '10',
    createdBy: 'Priya Shah',
    lastUpdatedBy: 'Priya Shah',
    createdOn: '2026-08-17T08:30:00+10:00',
    lastUpdatedOn: '2026-08-17T08:30:00+10:00',
    updatedBy: 'Priya Shah',
    updatedOn: '2026-08-17T08:30:00+10:00',
  },
  {
    ...createDefaultSearchMatchingForm(),
    id: 'SMR-0001',
    ruleName: 'Keyword Search',
    ruleArea: 'keyword_search',
    searchSources: ['applied_keywords', 'post_title', 'post_description'],
    createdBy: 'Alex Morgan',
    lastUpdatedBy: 'Alex Morgan',
    createdOn: '2026-08-17T08:20:00+10:00',
    lastUpdatedOn: '2026-08-17T08:20:00+10:00',
    updatedBy: 'Alex Morgan',
    updatedOn: '2026-08-17T08:20:00+10:00',
  },
];

export const getListActionsForRule = () => ['view', 'edit'];
